import { JSX } from 'react';
import { Plan } from 'subscription-service-client';
import { twMerge } from 'tailwind-merge';

import { Badge } from '@videoblocks/design-system';
import { LoggerFactory } from '@videoblocks/typescript-logger';

import { ENHANCED_CALLOUT_PLAN_CODES } from '../../utils/planUtils';
import { useCheckoutContext } from './CheckoutContext';

const logger = LoggerFactory.create('PlanSelectionBox');

export interface PlanSelectionBoxProps {
    isSelected: boolean;
    title: string;
    plan: Plan;
    showDiscountBadge?: boolean;
    onClick: (planCode: string) => void;
}

const getPlanIntervalString = (plan: Plan) => {
    // Question: Will monthly plans be defined as 1 month or 30 days?
    //   Similarly, will yearly plans be defined as 1 year, 12 months, or 365 days?
    if (
        (plan.intervalUnit == 'days' && plan.intervalLength == 365) ||
        (plan.intervalUnit == 'months' && plan.intervalLength == 12)
    ) {
        return 'annually';
    }
    return 'monthly';
};

const isEnhancedAnnualPlan = (plan: Plan): boolean => {
    if (!plan.code) return false;
    const isAnnual = getPlanIntervalString(plan) === 'annually';
    return isAnnual && ENHANCED_CALLOUT_PLAN_CODES.has(plan.code);
};

const getMonthlyEquivalent = (annualPrice: number): string => {
    return (annualPrice / 12).toFixed(2);
};

export default function PlanSelectionBox({
    isSelected,
    title,
    plan,
    onClick,
    showDiscountBadge,
}: PlanSelectionBoxProps): JSX.Element {
    const { monthlyPlan, currency, currencySymbol, setCurrency } = useCheckoutContext();

    const getPlanPrice = (plan: Plan, currency: string) => {
        const currencies = plan.currencies;
        const foundCurrency = currencies?.filter((planCurrency) => planCurrency.currency === currency)[0];
        if (foundCurrency && foundCurrency.unitAmount) {
            return foundCurrency;
        }
        const usdFallBack = currencies?.filter((planCurrency) => planCurrency.currency === 'USD')[0];
        if (usdFallBack && usdFallBack.unitAmount) {
            setCurrency('USD');
            return usdFallBack;
        }
        return {
            currency: 'USD',
            unitAmount: 0.0,
        };
    };

    const planCurrency = getPlanPrice(plan, currency);

    const calculateSavings = () => {
        if (!monthlyPlan || getPlanIntervalString(plan) !== 'annually') {
            return null;
        }
        const monthlyPrice = getPlanPrice(monthlyPlan, currency).unitAmount ?? 0;
        const annualPrice = getPlanPrice(plan, currency).unitAmount ?? 0;
        const savings = monthlyPrice * 12 - annualPrice;
        return savings > 0 ? savings.toFixed(2) : null;
    };

    const handleClick = () => {
        // plan.code shouldn't be null, but just in case
        if (!plan?.code) {
            logger.error({ plan, title }, 'Plan code is missing or invalid');
            throw new Error('Plan code is missing or invalid.');
        }
        onClick(plan.code);
    };

    const savingsAmount = calculateSavings();

    const calculateSavingsPercentage = (): number | null => {
        if (!monthlyPlan || getPlanIntervalString(plan) !== 'annually') return null;
        const monthlyPrice = getPlanPrice(monthlyPlan, currency).unitAmount ?? 0;
        const annualPrice = getPlanPrice(plan, currency).unitAmount ?? 0;
        if (monthlyPrice <= 0) return null;
        const percentage = Math.round(((monthlyPrice * 12 - annualPrice) / (monthlyPrice * 12)) * 100);
        return percentage > 0 ? percentage : null;
    };

    const showEnhancedCallout = showDiscountBadge && isEnhancedAnnualPlan(plan);
    const savingsPercentage = showEnhancedCallout ? calculateSavingsPercentage() : null;

    return (
        <div
            className={twMerge(
                'w-full max-h-28 flex flex-row justify-between items-center bg-white rounded-sm p-4 border-2 border-white hover:border-blue-200',
                isSelected && 'border-blue-500 hover:border-blue-500',
                !isSelected && 'cursor-pointer',
            )}
            onClick={handleClick}
        >
            <div className="flex flex-col justify-center">
                <div className="flex items-center gap-1.5">
                    <h3 className="text-base font-bold">{title}</h3>
                    {showEnhancedCallout && savingsPercentage && (
                        <Badge
                            color="success"
                            size="small"
                            shape="square"
                            text={`Save ${savingsPercentage}% with Annual`}
                            fontWeight="bold"
                            className="-mt-1.5"
                        />
                    )}
                </div>
                <p className="text-xs">
                    {currencySymbol}
                    {planCurrency.unitAmount} billed {getPlanIntervalString(plan)}
                    {showEnhancedCallout && planCurrency.unitAmount ? (
                        <span>
                            {' '}
                            ({currencySymbol}
                            {getMonthlyEquivalent(planCurrency.unitAmount)} per month)
                        </span>
                    ) : null}
                </p>
                {getPlanIntervalString(plan) === 'annually' && savingsAmount && (
                    <p className="mt-0 text-xs font-bold text-green-600">
                        Save {currencySymbol}
                        {savingsAmount}/year
                    </p>
                )}
            </div>
            <div
                className={twMerge(
                    'flex items-center justify-center w-6 h-6 border-2 rounded-full',
                    isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300 bg-white hover:border-blue-300',
                )}
            >
                {isSelected && <div className="w-2 h-2 bg-white rounded-full"></div>}
            </div>
        </div>
    );
}
